import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import React, { useCallback } from 'react';
import { SxProps, useTheme } from '@mui/material/styles';
import { truncateIfTooLong } from 'src/util/truncate';
import { GradientIconButton } from '../gradients/GradientIconButton';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useWizardDialogConfirmation } from 'src/hooks/wizard/useWizardDialogConfirmation';
import { useAuth } from 'src/contexts/AuthContext';
import { removeWalletAddress } from 'src/util/removeWalletAddress';
import { useDisconnect } from 'wagmi';
import { ResetUserItemsParams } from 'functions/src/callable/user/resetUserItems.f';
import Typography from '@mui/material/Typography';
export type MenuItemWalletProps = Omit<MenuItemProps, 'onClick' | 'sx'> & {
  address: string;
  onClick: () => Promise<void>;
  isSelected: boolean;
  menuItemSx?: SxProps;
};

export function MenuItemWallet({
  address,
  onClick,
  isSelected,
  menuItemSx,
  ...rest
}: MenuItemWalletProps) {
  const theme = useTheme();
  const { open: openConfirmationWizardDialog } = useWizardDialogConfirmation();
  const { disconnect } = useDisconnect();
  const { userData } = useAuth();
  const userId = userData?.id;
  const onConfirm = async () => {
    if (
      !userId ||
      !userData?.addresses?.length ||
      userId.toLowerCase() === address
    ) {
      return;
    }
    try {
      await removeWalletAddress(userId, address);
      disconnect();
      // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
      const { resetUserItems } = await import(
        'src/firebaseCloud/user/resetUserItems'
      );
      const resetUserItemsParams: ResetUserItemsParams = { userId: userId };
      await resetUserItems(resetUserItemsParams);
      // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
      const successOutcome = [
        ['message', `Address removed successfully!`],
        ['success', 'true'],
      ];
      return successOutcome;
    } catch (error) {
      // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
      const errorOutcome = [
        ['message', 'Error removing wallet address. Please try again.'],
        ['success', 'false'],
      ];
      return errorOutcome;
    }
  };
  const onCancel = async () => {
    return undefined;
  };
  const onClickHandler = useCallback(
    (event) => {
      event.stopPropagation();
      openConfirmationWizardDialog({
        onConfirm,
        onCancel,
        title: 'Remove Wallet Address',
        description: `Remove ${truncateIfTooLong(address)} from your account?`,
        open: true,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openConfirmationWizardDialog],
  );

  return (
    <MenuItem
      onClick={
        !isSelected
          ? async () => {
              await onClick();
            }
          : undefined
      }
      sx={{
        justifyContent: 'space-between',
        backgroundColor: isSelected
          ? theme.palette.background.elevation[3]
          : undefined,
        ...menuItemSx,
      }}
      {...rest}
    >
      <Typography
        color="primary.light"
        variant="body1"
        sx={{ fontWeight: 500 }}
      >
        {truncateIfTooLong(address)}
      </Typography>
      {userId?.toLowerCase() !== address && (
        <GradientIconButton
          edge="start"
          color="inherit"
          disableMobile
          aria-label="remove"
          size="small"
          onClick={onClickHandler}
          IconComponent={LinkOffIcon}
          sxOuter={{ p: 0 }}
        />
      )}
    </MenuItem>
  );
}
